import { ContactStandard } from '@cms/ui';
import type { ContactStandardProps } from '@cms/ui';
import type { NodeContact } from '@hub/models/api/node-contact.types';
import type { Contacts } from '@hub/libs/storyblok/types';
import appendQuery from 'append-query';
import { useRouter } from 'next/router';
import { getLocale } from '@hub/libs/constants';
import { IGUS_REWRITE } from '@hub/libs/redirect.helper';

// adre query param needs to be appended to this for the default igus contact form
const DEFAULT_CONTACT_FORM_RELATIVE_URL = `/myigus/default.aspx?page=form_contact`;

export type ContactBoxProps = Omit<NodeContact, 'email'> & {
  /**
   * Contact standard texts (headlines, opening hours and info text) coming from the
   * Storyblok CMS `/data/contacts` endpoint. Rendered by the shared `ContactStandard`
   * component so the contact information has a single, CMS-managed source of truth.
   */
  contactStandardTexts?: Contacts;
};

/**
 * `content` payload of a Storyblok `contact` component, covering the contact-person
 * fields the `ContactStandard` component reads from its `tag` story.
 */
type ContactPersonContent = {
  component: 'contact';
  first_name: string;
  last_name: string;
  phone?: string;
  image: Array<{
    image: { item: { embed_link: string; focal_embed_link: string } };
    alt_text?: string;
    copyright: string;
  }>;
};

/**
 * Maps a legacy MMA `NodeContact` into the Storyblok story shape expected by the
 * `ContactStandard` component's `tag` prop, so the existing contact person (name,
 * photo and phone number) is displayed by the shared CMS component.
 */
export const mapNodeContactToContactStory = ({
  id,
  name,
  picture,
  phone,
}: Pick<NodeContact, 'id' | 'name' | 'picture' | 'phone'>): ContactStandardProps['tag'] => {
  const [firstName, ...rest] = (name ?? '').trim().split(' ');
  const lastName = rest.join(' ');

  const content: ContactPersonContent = {
    component: 'contact',
    first_name: firstName,
    last_name: lastName,
    phone,
    image: picture
      ? [
          {
            image: { item: { embed_link: picture, focal_embed_link: picture } },
            alt_text: name,
            copyright: '',
          },
        ]
      : [],
  };

  // `tag` expects a full Storyblok `ISbStoryData`; we only populate the fields the
  // ContactStandard component reads, so cast at this CMS boundary.
  return { uuid: `${id}`, content } as unknown as ContactStandardProps['tag'];
};

export const ContactBox = ({ id, name, picture, phone, contactFormUrl, contactStandardTexts }: ContactBoxProps) => {
  const router = useRouter();
  const locale = getLocale(router);

  if (!name) {
    return null;
  }

  const defaultContactFormUrl = IGUS_REWRITE(DEFAULT_CONTACT_FORM_RELATIVE_URL, locale);
  const contactFormSlug = contactFormUrl || appendQuery(defaultContactFormUrl, { adre: `${id}` });

  const tag = mapNodeContactToContactStory({ id, name, picture, phone });
  const headlinesText = contactStandardTexts?.[0]?.content ? [contactStandardTexts[0].content] : [];
  const defaultText = contactStandardTexts ?? [];

  return (
    <div data-testid="contact-box" className="mt-20">
      <ContactStandard
        view={false}
        tag={tag}
        headlines_text={headlinesText}
        default_text={defaultText}
        default_form_slug={contactFormSlug}
      />
    </div>
  );
};
